import { useState, useEffect, useRef } from "react";
import { useT } from "../lib/i18n";
import { META } from "../lib/content";
import { Send } from "lucide-react";
import { toast } from "sonner";

export default function ContactForm() {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const webhookUrl = process.env.REACT_APP_N8N_CONTACT_WEBHOOK || META?.contactWebhookUrl || "https://automation.voxianet.com/webhook/bizcon-contact";
  const sourceDomain = META?.domain || "bizcon.voxianet.com";
  const turnstileAction = META?.turnstileAction || "bizcon_contact";

  useEffect(() => {
    let isMounted = true;
    if (!siteKey || !containerRef.current) return;

    const scriptId = "cf-turnstile-script";

    const renderWidget = () => {
      if (!isMounted || !containerRef.current || !window.turnstile) return;
      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {}
        widgetIdRef.current = null;
      }
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: turnstileAction,
          theme: "light",
          callback: (token) => {
            if (isMounted) setTurnstileToken(token);
          },
          "expired-callback": () => {
            if (isMounted) setTurnstileToken(null);
          },
          "error-callback": () => {
            if (isMounted) setTurnstileToken(null);
          },
        });
      } catch (e) {
        console.error("Turnstile render error", e);
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.getElementById(scriptId);
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", renderWidget);
    }

    return () => {
      isMounted = false;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {}
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, turnstileAction]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (siteKey && !turnstileToken) {
      toast.error(t("contact.err_turnstile") || "Proszę ukończyć weryfikację antybotową.");
      return;
    }

    setSending(true);
    try {
      const res = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          "cf-turnstile-response": turnstileToken,
          source: sourceDomain,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error ${res.status}`);
      }

      toast.success(t("contact.success"));
      setForm({ name: "", phone: "", email: "", message: "" });
      setTurnstileToken(null);
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch (_) {}
      }
    } catch (err) {
      toast.error(t("contact.err_generic") || "Wystąpił błąd podczas wysyłania wiadomości.");
    }
    setSending(false);
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-[#F2EBE5] border-y border-[#E2D8CF]">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="eyebrow text-[#6E1A24] mb-3">{t("contact.eyebrow")}</div>
          <h2 className="heading-poster text-4xl md:text-5xl">{t("contact.title")}</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-ink2 mb-2">{t("contact.l_name")}</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t("contact.p_name")}
                required
                className="w-full px-4 py-3 bg-[#F8F5F2] border border-[#E2D8CF] text-[#1A1A1A] placeholder:text-ink2/50 focus:outline-none focus:border-[#6E1A24] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-ink2 mb-2">{t("contact.l_phone")}</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder={t("contact.p_phone")}
                required
                className="w-full px-4 py-3 bg-[#F8F5F2] border border-[#E2D8CF] text-[#1A1A1A] placeholder:text-ink2/50 focus:outline-none focus:border-[#6E1A24] transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-ink2 mb-2">{t("contact.l_email")}</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder={t("contact.p_email")}
              required
              className="w-full px-4 py-3 bg-[#F8F5F2] border border-[#E2D8CF] text-[#1A1A1A] placeholder:text-ink2/50 focus:outline-none focus:border-[#6E1A24] transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-ink2 mb-2">{t("contact.l_message")}</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder={t("contact.p_message")}
              rows={4}
              required
              className="w-full px-4 py-3 bg-[#F8F5F2] border border-[#E2D8CF] text-[#1A1A1A] placeholder:text-ink2/50 focus:outline-none focus:border-[#6E1A24] transition-colors resize-none"
            />
          </div>

          {siteKey && (
            <div className="flex justify-center my-4">
              <div ref={containerRef} data-testid="cf-turnstile-container" />
            </div>
          )}

          <div className="text-center">
            <button
              type="submit"
              disabled={sending}
              className="btn-primary px-8 py-4 inline-flex items-center gap-2"
            >
              {sending ? "..." : t("contact.submit")} <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
